import { Suspense, useEffect, useState } from 'react';
import { camelCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import { Select as InfiniteScrollSelect } from 'components/shared/form/InfiniteScrollDropdown';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import LoadingOverlay from 'components/shared/table/LoadingOverlay';
import Tooltip from 'components/shared/Tooltip';
import { useProfile } from 'context/profile';
import Upload from 'hooks/useDirectUpload/Upload';
import useMutateWithToast from 'hooks/useMutateWithToast';
import AIPrompt from 'models/AIPrompt';
import { ClientType } from 'models/Client';
import Summarization from 'models/Summarization';
import { aiPromptsQueryKeys, indexAiPrompts, useInfiniteAdminAiPrompts } from 'services/api/aiPrompts';
import { createSummarization } from 'services/api/summarizations';
import { useMobileNavigationActions } from 'stores/mobileNavigationStore';
import theme from 'styles/theme/theme';
import { BodySmall, BodySmallBold, H1 } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';
import ArrowRight from 'svg/ArrowRight';
import HamburgerMenuIcon from 'svg/HamburgerMenuIcon';
import PurpleClippy from 'svg/PurpleClippy';

const CREATE_BUTTON_ID = 'create';
const CREATE_WORKSPACE_URL = 'https://www.olio.health/summarization-workspace-form';

const TARGET_CLASSNAME_LABELS: Record<string, string> = {
  'Review::Authorization': 'Concurrent Review',
  Summarization: 'Workspace',
  'Review::Referral': 'Referral',
};

function NewSummarization() {
  const { profile } = useProfile();
  const { setNavigationOpen } = useMobileNavigationActions();
  const navigate = useNavigate();
  const [selectedPromptId, setSelectedPromptId] = useState<string>(''); // For regular user pills
  const [selectedPrompt, setSelectedPrompt] = useState<AIPrompt | null>(null); // For admin dropdown
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const isActingAsAdmin = profile.actingClient.clientType === ClientType.ADMIN;

  const regularQueryParams = {
    'displayName.distinct': 'true',
    archived: false,
    sortBy: 'displayName',
    type: 'summary',
    client: profile.actingClientId,
    targetClassname: 'Summarization',
  };

  const adminQueryParams = {
    archived: false,
    sortBy: 'targetClassname,displayName',
    type: 'summary',
    'prompts.distinct': true,
  };

  // Regular users: use query for pill display (small dataset)
  const { data: regularAiPromptsData } = useQuery({
    queryKey: ['newSummarizationPage', 'regular', aiPromptsQueryKeys.index(regularQueryParams)],
    queryFn: ({ signal }) => indexAiPrompts({ ...regularQueryParams }, signal),
    enabled: !isActingAsAdmin,
  });

  // Admin users: use infinite query for dropdown with infinite scroll
  const { options: adminInfiniteOptions, onDebouncedSearch } = useInfiniteAdminAiPrompts(adminQueryParams);

  useEffect(() => {
    if (isActingAsAdmin) {
      adminInfiniteOptions.refetch();
    }
  }, [isActingAsAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: createSummarization,
    onSuccess: (summarization: Summarization) => {
      navigate(`/summarizations/${summarization.id}`);
    },
  });

  const handleGenerateSummary = () => {
    const aiPromptId = isActingAsAdmin ? selectedPrompt?.id : selectedPromptId;
    if (!aiPromptId) return;

    mutate({
      aiPromptId,
      attachments: uploads,
    });
  };

  const handleUploadsChange = (newUploads: Upload[]) => {
    setUploads(newUploads);
  };

  const handlePillClick = (id: string) => {
    if (id === CREATE_BUTTON_ID) {
      window.open(CREATE_WORKSPACE_URL, '_blank');
    } else {
      setSelectedPromptId(id);
    }
  };

  const promptButtons = [
    ...(regularAiPromptsData?.data || []).map((prompt) => ({
      id: prompt.id,
      label: prompt.displayName,
      tag: camelCase(prompt.displayName),
      description: prompt.description,
    })),
    { id: CREATE_BUTTON_ID, label: '+ Create', tag: 'createSummarizationType', description: 'Add your own use case' },
  ];

  // Set default selection for regular users (admin uses InfiniteScrollSelect's populateDefault)
  useEffect(() => {
    if (!isActingAsAdmin && !selectedPromptId && regularAiPromptsData?.data && regularAiPromptsData.data.length > 0) {
      setSelectedPromptId(regularAiPromptsData.data[0].id);
    }
  }, [regularAiPromptsData, selectedPromptId, isActingAsAdmin]);

  const hasSelectedPrompt = isActingAsAdmin ? selectedPrompt !== null : selectedPromptId !== '';
  const canStartSummarizing = uploads.length > 0 && hasSelectedPrompt && !isUploading;

  return (
    <>
      <ContentContainer>
        <MobileOnly>
          <MobileNavMenu variant='icon' onClick={() => setNavigationOpen(true)} data-cy='hamburgerIconButton'>
            <HamburgerMenuIcon />
          </MobileNavMenu>
        </MobileOnly>
        <Content>
          <HeaderSection>
            <Title>
              <AISparkleIcon width={30} height={29} />
              Summarize for <GradientText>quicker</GradientText> decision-making
            </Title>
            <Subtitle>Optimize your decision-making process by attaching patient documentation to summarize</Subtitle>
          </HeaderSection>

          <TypeSelectorSection>
            <BodySmallBold>Select a summarization type to guide our prompt for better results:</BodySmallBold>
            {isActingAsAdmin ? (
              <AdminPromptSelector>
                <AdminSelectContainer>
                  <InfiniteScrollSelect<AIPrompt>
                    options={adminInfiniteOptions}
                    value={selectedPrompt}
                    onChange={setSelectedPrompt}
                    getOptionValue={(prompt) => prompt.id}
                    getOptionLabel={(prompt) => prompt.displayName || prompt.name}
                    groupBy={(prompt) => prompt.targetClassname || ''}
                    getGroupOptionLabel={(key) => TARGET_CLASSNAME_LABELS[key] || key}
                    placeholder='Select a prompt...'
                    onDebouncedSearch={onDebouncedSearch}
                    populateDefault
                    data-cy='adminPromptSelect'
                  />
                </AdminSelectContainer>
              </AdminPromptSelector>
            ) : (
              <TypePills>
                {promptButtons.map((button) => (
                  <Tooltip key={button.id} text={button.description} disabled={!button.description} position='top'>
                    <TypePill
                      $selected={selectedPromptId === button.id}
                      data-cy={button.tag}
                      onClick={() => handlePillClick(button.id)}>
                      <TypePillText $selected={selectedPromptId === button.id} data-text={button.label}>
                        {button.label}
                      </TypePillText>
                    </TypePill>
                  </Tooltip>
                ))}
              </TypePills>
            )}
          </TypeSelectorSection>

          <FileUploadContainer>
            <FileInputComponent
              onChange={handleUploadsChange}
              onUploading={setIsUploading}
              uploadList='inside'
              draggingStyles={{
                backgroundColor: theme.colors.blue5,
                borderColor: theme.colors.blue100,
              }}
              Icon={() => <PurpleClippy />}
            />
          </FileUploadContainer>

          <StartButton
            variant='primary'
            onClick={handleGenerateSummary}
            disabled={!canStartSummarizing}
            loading={isPending || isUploading}>
            <AISparkleIcon width={14} height={14} color='white' />
            <BodySmallBold>Start Summarizing</BodySmallBold>
          </StartButton>
        </Content>

        <Footer>
          <ViewHistoryButton variant='outline-primary' onClick={() => navigate('/summarizations')}>
            <ButtonText>View History</ButtonText>
            <ArrowRight />
          </ViewHistoryButton>
        </Footer>
      </ContentContainer>
      <FooterBackground />
    </>
  );
}

export default function NewSummarizationPage() {
  return (
    <Suspense fallback={<Loading />}>
      <NewSummarization />
    </Suspense>
  );
}

const Loading = styled(LoadingOverlay)`
  position: relative;
`;

const MobileNavMenu = styled(Button)`
  position: absolute;
  top: 24px;
  left: 24px;
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.white};
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(183, 137, 220, 0.1) 70%,
    rgba(183, 137, 220, 0.15) 80%,
    rgba(159, 98, 208, 0.25) 100%
  );
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 40px 80px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled(H1)`
  font-weight: 700;
  font-size: 30px;
  margin: 0 0 16px 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;

  > svg {
    margin-top: 4px;
    margin-right: 10px;
    flex-shrink: 0;
  }
`;

const GradientText = styled.span`
  background: ${theme.colors.ai};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(BodySmall)`
  line-height: 22px;
  color: ${theme.colors.black75};
`;

const TypeSelectorSection = styled.div`
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const TypePills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const TypePill = styled.button<{ $selected: boolean }>`
  padding: 8px 16px;
  border-radius: 36px;
  cursor: pointer;
  border: 1px solid;

  ${({ $selected }) =>
    $selected
      ? `
    background: ${theme.colors.ai10};
    border-color: ${theme.colors.purple100};
  `
      : `
    background: ${theme.colors.white};
    border-color: ${theme.colors.black25};

    &:hover {
      border-color: ${theme.colors.black50};
    }
  `}
`;

const TypePillText = styled.span<{ $selected: boolean }>`
  font-size: 12px;
  display: inline-block;
  position: relative;

  /* Reserve space for bold text using pseudo-element */
  &::before {
    content: attr(data-text);
    font-weight: 500;
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    display: block;
  }

  ${({ $selected }) =>
    $selected
      ? `
    font-weight: 500;
    background: ${theme.colors.ai};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `
      : `
    font-weight: 400;
    color: ${theme.colors.black100};
  `}
`;

const AdminPromptSelector = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 600px;
`;

const AdminSelectContainer = styled.div`
  flex: 1;
  min-width: 300px;
`;

const FileUploadContainer = styled.div`
  width: 100%;
  max-width: 792px;
  margin-bottom: 40px;
`;

const StartButton = styled(Button)`
  background: ${theme.colors.ai};
  border: none;
  min-width: 200px;

  &:disabled {
    opacity: 0.25;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(90deg, ${theme.colors.purpleHover} 0%, ${theme.colors.purpleExtraDark} 100%);
  }
`;

const Footer = styled.div`
  width: 100%;
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FooterBackground = styled.div`
  width 100%;
  height: 100%;
  background: ${theme.colors.ai20};
`;

const ViewHistoryButton = styled(Button)`
  border-radius: 20px;
  min-width: 179px;
  border-color: ${theme.colors.purple100};
  background: ${theme.colors.white};
  padding: 8px 24px;

  &:hover {
    border-color: ${theme.colors.purpleHover};
    background: ${theme.colors.white};
  }

  &:focus {
    border-color: ${theme.colors.purple100};
    background: ${theme.colors.white};
  }
`;

const ButtonText = styled(BodySmallBold)`
  background: ${theme.colors.ai};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
`;
